import {useGetUserSchedules} from "../../../react-query-hooks/schedule";
import type {CreateUserScheduleApiResponse} from "../../../react-query-hooks/schedule/types";
import {
  resolveScheduleSelection,
  sanitizeSchedules,
  selectPreferredSchedule,
} from "../domain";

/**
 * Fixed page args so every caller shares one query key.
 *
 * The offering forms and the availability panel inside them both read this list. If their
 * args drifted apart React Query would treat them as two separate queries and fire two
 * requests for the same data — and worse, could resolve two different "preferred"
 * schedules. Everything goes through this hook so the args cannot diverge.
 */
export const OFFERING_SCHEDULES_PAGE = 1;
export const OFFERING_SCHEDULES_PER_PAGE = 50;

export type OfferingSchedulesResult = {
  schedules: CreateUserScheduleApiResponse[];
  preferredSchedule: CreateUserScheduleApiResponse | null;
  isLoading: boolean;
  isError: boolean;
  errorMessage: string | null;
  /** Loaded successfully and the coach owns no schedules — blocking and actionable. */
  hasNoSchedules: boolean;
  /**
   * The schedule id a form should start on.
   *
   * Returns `selectedScheduleId` untouched until the list has loaded, so an in-flight
   * fetch never clears a restored draft's selection.
   */
  resolveSelection: (selectedScheduleId?: string | null) => string;
};

export function useOfferingSchedules(): OfferingSchedulesResult {
  const query = useGetUserSchedules(OFFERING_SCHEDULES_PAGE, OFFERING_SCHEDULES_PER_PAGE);

  const schedules = sanitizeSchedules(query.data?.schedules);
  const isLoaded = query.isSuccess;

  return {
    schedules,
    preferredSchedule: selectPreferredSchedule(schedules),
    isLoading: query.isLoading,
    isError: query.isError,
    errorMessage: query.error?.message ?? null,
    hasNoSchedules: isLoaded && schedules.length === 0,
    resolveSelection: (selectedScheduleId) =>
      isLoaded
        ? resolveScheduleSelection({schedules, selectedScheduleId})
        : selectedScheduleId ?? "",
  };
}
